/**
 * MetaOAuthPopupManager
 *
 * Gerencia o ciclo de vida completo de um popup OAuth da Meta:
 * - Abre popup centralizado (ou redireciona no mobile)
 * - Detecta fechamento do popup
 * - Polling de status no backend após o callback
 * - Listener de postMessage para comunicação cross-origin
 * - Timeout automático
 * - Cleanup garantido
 *
 * Uso:
 *   const manager = new MetaOAuthPopupManager({ provider: 'whatsapp', ... });
 *   manager.open(oauthUrl);
 *   manager.onSuccess(callback);
 *   manager.onError(callback);
 *   manager.destroy(); // limpa tudo
 */

const POPUP_WIDTH = 600;
const POPUP_HEIGHT = 700;
const POLL_INTERVAL_MS = 2000;
const TIMEOUT_MS = 5 * 60 * 1000; // 5 minutos

export class MetaOAuthPopupManager {
  constructor({ provider, pollFn, onSuccess, onError, onClose }) {
    this.provider = provider;
    this.pollFn = pollFn;         // async () => { status, ... } — chama GET /auth/meta/status
    this.onSuccessCb = onSuccess; // (statusData) => void
    this.onErrorCb = onError;     // (error) => void
    this.onCloseCb = onClose;     // () => void — popup fechado manualmente

    this._popup = null;
    this._pollTimer = null;
    this._timeoutTimer = null;
    this._messageHandler = null;
    this._closedCheckTimer = null;
    this._destroyed = false;
  }

  // ── Detecta mobile ────────────────────────────────────────────────────────
  static isMobile() {
    return /Android|iPhone|iPad|iPod|BlackBerry|Opera Mini|IEMobile|WPDesktop/i.test(
      navigator.userAgent
    );
  }

  // ── Calcula posição centralizada para o popup ─────────────────────────────
  static _popupDimensions() {
    const left = Math.round(window.screenX + (window.outerWidth - POPUP_WIDTH) / 2);
    const top = Math.round(window.screenY + (window.outerHeight - POPUP_HEIGHT) / 2.5);
    return `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top}`;
  }

  // ── Abre o popup OAuth ────────────────────────────────────────────────────
  open(url) {
    if (this._destroyed) return;

    // Mobile: fallback para redirect completo
    if (MetaOAuthPopupManager.isMobile()) {
      sessionStorage.setItem("meta_oauth_return", window.location.pathname);
      window.location.href = url;
      return;
    }

    const features = `${MetaOAuthPopupManager._popupDimensions()},scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no`;
    this._popup = window.open(url, "meta_oauth_popup", features);

    if (!this._popup || this._popup.closed) {
      // Popup bloqueado pelo browser
      this.onErrorCb?.("popup_blocked");
      return;
    }

    this._popup.focus();
    this._startListeners();
    this._startTimeout();
  }

  // ── Inicia listeners ──────────────────────────────────────────────────────
  _startListeners() {
    // 1. postMessage: o backend pode enviar mensagem após o callback
    this._messageHandler = (event) => {
      // Aceita mensagens de qualquer origem para compatibilidade
      try {
        const { type, provider, status } = event.data || {};
        if (type === "meta_oauth_callback" && provider === this.provider) {
          if (status === "success") {
            this._handleSuccess();
          } else {
            this._handleError(event.data.error || "oauth_failed");
          }
        }
      } catch {}
    };
    window.addEventListener("message", this._messageHandler);

    // 2. Polling do fechamento do popup (fallback quando postMessage não dispara)
    this._closedCheckTimer = setInterval(() => {
      if (this._popup?.closed) {
        this._onPopupClosed();
      }
    }, 500);
  }

  // ── Popup foi fechado pelo usuário ou pelo callback ───────────────────────
  _onPopupClosed() {
    this._clearClosedCheck();
    if (this._destroyed) return;

    // Aguarda um ciclo e então faz poll do status
    // (o callback pode ter sido processado logo antes do close)
    setTimeout(() => this._pollStatus(), 800);
  }

  // ── Polling do status no backend ──────────────────────────────────────────
  async _pollStatus() {
    if (this._destroyed) return;
    try {
      const statusData = await this.pollFn();
      const providerStatus = statusData?.[this.provider]?.status;
      if (providerStatus === "connected" || providerStatus === "connected_mock") {
        this._handleSuccess(statusData?.[this.provider]);
        return;
      }
    } catch {}

    // Continua fazendo poll enquanto o popup estiver aberto
    if (this._popup && !this._popup.closed) {
      this._pollTimer = setTimeout(() => this._pollStatus(), POLL_INTERVAL_MS);
    } else {
      // Popup fechado sem sucesso — usuário cancelou
      if (!this._destroyed) {
        this.onCloseCb?.();
        this.destroy();
      }
    }
  }

  // ── Timeout global ────────────────────────────────────────────────────────
  _startTimeout() {
    this._timeoutTimer = setTimeout(() => {
      if (!this._destroyed) {
        this._handleError("timeout");
      }
    }, TIMEOUT_MS);
  }

  // ── Sucesso ───────────────────────────────────────────────────────────────
  _handleSuccess(statusData) {
    if (this._destroyed) return;
    this._popup?.close();
    this.onSuccessCb?.(statusData);
    this.destroy();
  }

  // ── Erro ──────────────────────────────────────────────────────────────────
  _handleError(error) {
    if (this._destroyed) return;
    this._popup?.close();
    this.onErrorCb?.(error);
    this.destroy();
  }

  // ── Cleanup ───────────────────────────────────────────────────────────────
  _clearClosedCheck() {
    if (this._closedCheckTimer) {
      clearInterval(this._closedCheckTimer);
      this._closedCheckTimer = null;
    }
  }

  destroy() {
    this._destroyed = true;
    this._clearClosedCheck();
    if (this._pollTimer) { clearTimeout(this._pollTimer); this._pollTimer = null; }
    if (this._timeoutTimer) { clearTimeout(this._timeoutTimer); this._timeoutTimer = null; }
    if (this._messageHandler) {
      window.removeEventListener("message", this._messageHandler);
      this._messageHandler = null;
    }
    if (this._popup && !this._popup.closed) {
      try { this._popup.close(); } catch {}
    }
    this._popup = null;
  }
}

/**
 * Hook React para uso simplificado do MetaOAuthPopupManager.
 *
 * Uso:
 *   const { connect, connecting } = useMetaOAuth({ provider: 'whatsapp', pollFn, onSuccess, onError });
 *   connect(oauthUrl);
 */
export function createMetaOAuthConnector({ provider, pollFn, onSuccess, onError, onClose }) {
  let manager = null;

  return {
    connect(url) {
      manager?.destroy();
      manager = new MetaOAuthPopupManager({
        provider,
        pollFn,
        onSuccess,
        onError,
        onClose,
      });
      manager.open(url);
    },
    cancel() {
      manager?.destroy();
      manager = null;
    },
  };
}
